import type { ReactNode } from 'react';
import type {
    RegimeDependenceProfileComparison,
    RegimeDependenceProfileReportRecord,
    RegimeDependenceProfileReportSummary
} from '@/types/appRouter';
import { FaLock } from 'react-icons/fa';
import { trpc } from '@/api/trpc';
import { Callout, Card, Disclosure, StatusBadge, Tooltip } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { useAppQuery } from '@/hooks/useAppQuery';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator';
import { RegimeRelativeEdgeChip } from './RegimeRelativeEdgeChip';
import { VERDICT_BADGE_TONES } from './RegimeVerdictHeader';
import {
    buildComparisonPreconditionRows,
    buildComparisonStateDeltaRows,
    buildPostLegBenchmarkNote,
    getComparisonAvailability,
    getComparisonDeltaRowNote,
    getComparisonDiagnosisPresentation,
    getComparisonRefusalPresentation,
    type ComparisonDeltaLeg,
    type ComparisonStateDeltaRow
} from './regimeComparisonPresentation';
import { formatInt, formatSignedPercent, getVerdictPresentation } from './regimeProfilePresentation';

const DELTA_NOTE_TONE_CLASSES = {
    negative: 'text-red-400',
    neutral: 'text-zinc-500',
    positive: 'text-green-400'
} as const;

function statesLine(states: readonly string[]): string {
    return states.length > 0 ? states.join(', ') : '—';
}

/**
 * A comparison the backend refused outright is a first-class honest state, not an error: the card names
 * the incompatibility in plain English, says what would unblock it, and always shows the backend's own
 * message. Unknown codes still render, naming the raw code — they never vanish.
 */
function ComparisonRefusalCard({ code, message, mismatchedFields }: { code: string; message: string; mismatchedFields: string[] }) {
    const presentation = getComparisonRefusalPresentation(code, mismatchedFields);

    return (
        <Card className="bg-zinc-900 border border-zinc-800">
            <div className="flex flex-wrap items-center gap-3">
                <StatusBadge className="uppercase tracking-wide" status="comparison_withheld" tone="neutral">
                    Comparison withheld
                </StatusBadge>
                <span className="text-sm text-zinc-300">{presentation.title}</span>
            </div>
            <p className="mt-2 text-sm text-zinc-400">{presentation.explanation}</p>
            <p className="mt-1 text-sm text-zinc-400">Unblock: {presentation.unblockHint}</p>
            <p className="mt-2 text-xs text-zinc-500">
                Backend refusal ({code}): {message}
            </p>
        </Card>
    );
}

/** One leg cell of the delta table: winsorized mean plus episodes-before-trades counts (design rule 1). */
function DeltaLegCell({ leg, adverse }: { leg: ComparisonDeltaLeg | null; adverse: boolean }) {
    if (leg === null) return <span className="text-zinc-600">—</span>;

    const meanClass = leg.belowEvaluationMinimum ? 'text-zinc-500' : leg.meanProfitPercent < 0 ? 'text-red-400' : 'text-green-400';
    const mean = <span className={`tabular-nums ${meanClass}`}>{formatSignedPercent(leg.meanProfitPercent)}</span>;

    return (
        <div>
            <div className="flex items-center justify-end gap-1.5">
                {adverse && <span className="text-[10px] font-medium uppercase tracking-wide text-amber-400">adverse</span>}
                {leg.belowEvaluationMinimum ? (
                    <Tooltip content="Below the per-slice evaluation minimum for this leg.">{mean}</Tooltip>
                ) : (
                    mean
                )}
                {/* The tooltip is hover-only; the below-minimum meaning must also reach screen readers. */}
                {leg.belowEvaluationMinimum && <span className="sr-only">below the per-slice evaluation minimum for this leg</span>}
            </div>
            <div className="text-right text-[11px] text-zinc-500">
                {formatInt(leg.episodeCount)} episodes · {formatInt(leg.tradeCount)} trades
            </div>
        </div>
    );
}

function DeltaRow({ row }: { row: ComparisonStateDeltaRow }) {
    const note = getComparisonDeltaRowNote(row);
    const delta = row.deltaMeanProfitPercent;

    return (
        <tr>
            <td className="py-1.5 pr-3 text-zinc-300">{row.state}</td>
            <td className="px-2 py-1.5 text-right">
                <DeltaLegCell adverse={row.adversePre} leg={row.pre} />
            </td>
            <td className="px-2 py-1.5 text-right">
                <DeltaLegCell adverse={row.adversePost} leg={row.post} />
            </td>
            <td className="px-2 py-1.5 text-right align-top">
                {delta === null ? (
                    <span className="text-zinc-600">—</span>
                ) : (
                    // An unevaluable row's delta stays visible for transparency but never carries a
                    // directional color; an exact zero is neutral, not green.
                    <span
                        className={`tabular-nums ${
                            !row.evaluable ? 'text-zinc-500' : delta < 0 ? 'text-red-400' : delta > 0 ? 'text-green-400' : 'text-zinc-400'
                        }`}
                    >
                        {formatSignedPercent(delta)}
                    </span>
                )}
            </td>
            <td className="py-1.5 pl-2 align-top">
                {note !== null && <span className={`text-xs ${DELTA_NOTE_TONE_CLASSES[note.tone]}`}>{note.text}</span>}
            </td>
        </tr>
    );
}

/** One column of the two-column diff: leg identity, then the verdict badge with its inseparable edge chip. */
function ComparisonLegColumn({
    title,
    identity,
    record
}: {
    title: string;
    identity: string;
    record: RegimeDependenceProfileReportRecord;
}) {
    const presentation = getVerdictPresentation(record.report.verdict);

    return (
        <div>
            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">{title}</div>
            <div className="mt-0.5 text-xs text-zinc-500">{identity}</div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <StatusBadge
                    className="uppercase tracking-wide"
                    status={record.report.verdict.verdict}
                    tone={VERDICT_BADGE_TONES[presentation.tone]}
                >
                    {presentation.label}
                </StatusBadge>
                <RegimeRelativeEdgeChip report={record.report} />
            </div>
        </div>
    );
}

function describePreLeg(record: RegimeDependenceProfileReportRecord): string {
    const parts = [
        'Fixed horizon',
        `${record.report.input.outcomeBasis ?? 'underlying'} basis`,
        ...(record.report.input.horizonSeconds !== null ? [formatTimeHorizon(record.report.input.horizonSeconds)] : []),
        `report #${record.id}`
    ];

    return parts.join(' · ');
}

function describePostLeg(record: RegimeDependenceProfileReportRecord): string {
    const parts = [
        'Realized strategy exit',
        ...(record.report.lineage.realizedInstrumentBasis !== null ? [`${record.report.lineage.realizedInstrumentBasis} instruments`] : []),
        ...(record.report.input.realizedOutcomeSetId !== null ? [`outcome set #${record.report.input.realizedOutcomeSetId}`] : []),
        `report #${record.id}`
    ];

    return parts.join(' · ');
}

function ComparisonBody({
    comparison,
    preRecord,
    postRecord
}: {
    comparison: RegimeDependenceProfileComparison;
    preRecord: RegimeDependenceProfileReportRecord;
    postRecord: RegimeDependenceProfileReportRecord;
}) {
    const diagnosisPresentation = getComparisonDiagnosisPresentation(comparison);
    const preconditionRows = buildComparisonPreconditionRows(comparison.preconditions);
    const deltaRows = buildComparisonStateDeltaRows({ comparison, postReport: postRecord.report, preReport: preRecord.report });
    const benchmarkNote = buildPostLegBenchmarkNote(postRecord.report);
    const attributable = comparison.diagnosis === 'entry_exit_interaction';

    return (
        <div className="space-y-4">
            <Card className="bg-zinc-900 border border-zinc-800">
                <div className="flex flex-wrap items-center gap-3">
                    <StatusBadge className="uppercase tracking-wide" status={comparison.diagnosis} tone={diagnosisPresentation.tone}>
                        {diagnosisPresentation.label}
                    </StatusBadge>
                    <span className="text-sm text-zinc-300">{diagnosisPresentation.gloss}</span>
                </div>
                {diagnosisPresentation.unblockHint !== null && (
                    <p className="mt-2 text-sm text-zinc-400">{diagnosisPresentation.unblockHint}</p>
                )}

                {/* Preconditions render as visible pass/fail rows ABOVE the diff, so a reader cannot
                    over-attribute a difference to the exit without seeing what gated attribution. */}
                <div className="mt-3 space-y-1.5 border-t border-zinc-800 pt-3">
                    {preconditionRows.map((row) => (
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-sm" key={row.key}>
                            <span aria-hidden className={row.passed ? 'w-4 text-green-400' : 'w-4 text-red-400'}>
                                {row.passed ? '✓' : '✗'}
                            </span>
                            <span className="sr-only">{row.passed ? 'Passed:' : 'Failed:'}</span>
                            <span className="min-w-40 text-zinc-300">{row.label}</span>
                            <span className={row.passed ? 'text-zinc-400' : 'font-medium text-red-300'}>{row.detail}</span>
                        </div>
                    ))}
                </div>
            </Card>

            <Card className="bg-zinc-900 border border-zinc-800">
                <div className="grid gap-4 md:grid-cols-2">
                    <ComparisonLegColumn identity={describePreLeg(preRecord)} record={preRecord} title="Pre-AERO" />
                    <ComparisonLegColumn identity={describePostLeg(postRecord)} record={postRecord} title="Post-AERO" />
                </div>

                <div className="mt-3 grid gap-x-4 gap-y-1 border-t border-zinc-800 pt-3 text-sm md:grid-cols-2">
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Adverse states:</span> {statesLine(comparison.preAeroAdverseStates)}
                    </div>
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Adverse states:</span> {statesLine(comparison.postAeroAdverseStates)}
                    </div>
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Veto states:</span> {statesLine(comparison.preAeroVetoStates)}
                    </div>
                    <div className="text-zinc-400">
                        <span className="text-zinc-500">Veto states:</span> {statesLine(comparison.postAeroVetoStates)}
                    </div>
                </div>
            </Card>

            {benchmarkNote !== null && (
                <Callout variant="warning">
                    {benchmarkNote} Candidate regime treatments can still be tested as inactive variants against their saved parent.
                </Callout>
            )}

            <Card className="bg-zinc-900 border border-zinc-800">
                <h4 className="text-sm font-medium text-zinc-100">Per-state change</h4>
                <p className="mt-0.5 text-xs text-zinc-500">
                    Winsorized mean profit per market composite state, pre vs post.
                    {!attributable && ' Not attributable to the AERO exit alone — see the diagnosis above.'}
                </p>
                <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-zinc-500">
                                <th className="py-1.5 pr-3 text-left font-medium">State</th>
                                <th className="px-2 py-1.5 text-right font-medium">Pre-AERO</th>
                                <th className="px-2 py-1.5 text-right font-medium">Post-AERO</th>
                                <th className="px-2 py-1.5 text-right font-medium">Δ mean</th>
                                <th className="py-1.5 pl-2 text-left font-medium">
                                    <span className="sr-only">Change</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {deltaRows.map((row) => (
                                <DeltaRow key={row.state} row={row} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            {comparison.reasons.length > 0 && (
                <Disclosure
                    title={<span className="text-xs text-zinc-500">Comparator reasons ({comparison.reasons.length})</span>}
                    variant="plain"
                >
                    <ul className="list-disc space-y-1 pl-5 text-xs text-zinc-400">
                        {comparison.reasons.map((reason, index) => (
                            <li key={`${index}-${reason}`}>{reason}</li>
                        ))}
                    </ul>
                </Disclosure>
            )}

            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                <FaLock className="h-3 w-3 shrink-0" />
                <span>
                    Both legs are training-window only — the walk-forward window (→{' '}
                    {preRecord.report.lineage.experimentWindow.walkForwardEndDateTime}) was never read.
                </span>
            </div>
        </div>
    );
}

interface RegimeComparisonSectionProps {
    experimentId: number;
    /** The panel's displayed main-leg record; it is the pre-AERO leg when it is a fixed-horizon report. */
    mainLegRecord: RegimeDependenceProfileReportRecord;
    /** This experiment's whole verdict history (server-filtered), newest first. */
    summaries: readonly RegimeDependenceProfileReportSummary[];
}

/**
 * Layer 5 — the post-AERO comparison leg: did the AERO exit absorb the regime dependence, or not?
 * The comparison is requested
 * over the two report ids actually rendered, so the diff and the delta table can never describe a
 * different pair of artifacts than the diagnosis.
 */
export function RegimeComparisonSection({ experimentId, mainLegRecord, summaries }: RegimeComparisonSectionProps) {
    const availability = getComparisonAvailability({ experimentId, summaries });
    const postSummary = availability.kind === 'ready' ? availability.postSummary : null;

    const postRecordQuery = useAppQuery(
        {
            ...trpc.regimeProfile.getById.queryOptions({ reportId: postSummary?.id ?? 0 }),
            enabled: postSummary !== null
        },
        { fallbackErrorMessage: 'Failed to load the post-AERO regime profile report' }
    );

    const postRecord = postRecordQuery.data?.status === 'ok' ? postRecordQuery.data.report : null;

    const comparisonQuery = useAppQuery(
        {
            ...trpc.regimeProfile.getComparison.queryOptions({
                postReportId: postRecord?.id ?? 0,
                preReportId: mainLegRecord.id
            }),
            enabled: postSummary !== null && postRecord !== null
        },
        { fallbackErrorMessage: 'Failed to load the pre/post-AERO comparison' }
    );

    let body: ReactNode;

    if (availability.kind === 'missingPostLeg') {
        body = (
            <Card className="bg-zinc-900">
                <p className="text-sm text-zinc-400">
                    This optional comparison needs a realized strategy-exit profile. Select available realized outcomes in the profile
                    controls when comparing exit effects would help your decision.
                </p>
            </Card>
        );
    } else if (availability.kind === 'missingPreLeg') {
        body = (
            <Card className="bg-zinc-900">
                <p className="text-sm text-zinc-400">
                    This optional comparison needs a fixed-horizon profile and a realized strategy-exit profile. Either profile can inform
                    research on its own.
                </p>
            </Card>
        );
    } else if (postRecordQuery.isLoading || (postRecord !== null && comparisonQuery.isLoading)) {
        body = (
            <Card className="bg-zinc-900">
                <DataLoadingSpinner text="Loading post-AERO comparison..." />
            </Card>
        );
    } else if (postRecordQuery.data?.status === 'unreadable') {
        body = (
            <Callout title="Stored post-AERO report cannot be read by this build" variant="warning">
                A realized strategy-exit report exists for this experiment, but this version of the app cannot decode it (
                {postRecordQuery.data.code}): {postRecordQuery.data.message}. Re-run the realized profile to produce a readable report.
            </Callout>
        );
    } else if (postRecord === null) {
        body = (
            <Callout title="Post-AERO report unavailable" variant="warning">
                A realized strategy-exit report is listed for this experiment, but it could not be loaded. Refresh to retry.
            </Callout>
        );
    } else if (comparisonQuery.data?.status === 'unreadable') {
        body = (
            <Callout title="Stored report cannot be read by this build" variant="warning">
                One of the comparison legs cannot be decoded by this version of the app ({comparisonQuery.data.code}):{' '}
                {comparisonQuery.data.message}. Re-run that profile to produce a readable report.
            </Callout>
        );
    } else if (comparisonQuery.data?.status === 'refused') {
        body = (
            <ComparisonRefusalCard
                code={comparisonQuery.data.code}
                message={comparisonQuery.data.message}
                mismatchedFields={comparisonQuery.data.mismatchedFields}
            />
        );
    } else if (comparisonQuery.data?.status === 'ok') {
        body = <ComparisonBody comparison={comparisonQuery.data.comparison} postRecord={postRecord} preRecord={mainLegRecord} />;
    } else {
        // The comparison query errored (toast already shown by useAppQuery) or has not produced data.
        body = (
            <Card className="bg-zinc-900">
                <p className="text-sm text-zinc-400">The pre/post-AERO comparison could not be loaded. Refresh to retry.</p>
            </Card>
        );
    }

    return (
        <section className="space-y-4">
            <div>
                <h3 className="text-base font-semibold text-white">Post-AERO comparison</h3>
                <p className="mt-0.5 text-xs text-zinc-500">
                    Did the AERO exit absorb the regime dependence, or not? Optional comparison. Test any proposed regime treatment as an
                    inactive family variant.
                </p>
            </div>
            {body}
        </section>
    );
}
